// src/api/comments.ts
const API_BASE: string = (process.env.REACT_APP_API_BASE || 'http://localhost:5000').replace(/\/$/, '');

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

export type CommentAuthor = {
    userId: string;
    username: string;
    displayName: string;
    avatarUrl: string;
};

export type Comment = {
    id: string;
    author: CommentAuthor;
    content: string;
    parentComment: string | null;
    replyToAuthor?: CommentAuthor | null;
    depth?: number;
    canReply?: boolean;
    replyCount: number;
    reactions?: { like?: number; love?: number };
    userReaction?: 'like' | 'love' | null;
    canEdit?: boolean;
    canDelete?: boolean;
    canReport?: boolean;
    editedAt?: string | null;
    createdAt: string;
    updatedAt: string;
};

export type FetchCommentsResult = {
    comments: Comment[];
    total: number;
    page: number;
    hasMore: boolean;
};

export async function fetchComments(
    postId: string,
    params: { page?: number; limit?: number; parent?: string; sort?: string; repliesOnly?: boolean } = {}
): Promise<FetchCommentsResult> {
    const url = new URL(`${API_BASE}/api/comments/${postId}`);
    if (params.page) url.searchParams.set('page', String(params.page));
    if (params.limit) url.searchParams.set('limit', String(params.limit));
    if (params.parent) url.searchParams.set('parent', params.parent);
    if (params.sort) url.searchParams.set('sort', params.sort);
    if (params.repliesOnly) url.searchParams.set('repliesOnly', 'true');

    const res = await fetch(url.toString(), { headers: authHeaders() });
    if (!res.ok) throw new Error(`Fetch comments failed: ${res.status}`);
    return res.json();
}

export async function createComment(
    postId: string,
    content: string,
    parentComment?: string | null
): Promise<Comment> {
    const res = await fetch(`${API_BASE}/api/comments/${postId}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ content, parentComment }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Create comment failed: ${res.status}`);
    }
    const data = await res.json();
    return data.comment;
}

export async function deleteComment(commentId: string): Promise<void> {
    const res = await fetch(`${API_BASE}/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Delete comment failed: ${res.status}`);
}

export async function updateComment(commentId: string, content: string): Promise<Comment> {
    const res = await fetch(`${API_BASE}/api/comments/${commentId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ content }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Update comment failed: ${res.status}`);
    }
    const data = await res.json();
    return data.comment;
}

export async function toggleCommentReaction(
    commentId: string,
    type: 'like' | 'love'
): Promise<{ reactions: { like?: number; love?: number }; userReaction: 'like' | 'love' | null }> {
    const res = await fetch(`${API_BASE}/api/comments/${commentId}/reactions`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ type }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Comment reaction failed: ${res.status}`);
    }
    return res.json();
}
